import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Postcode from 'react-daum-postcode';
import { useDispatch, useSelector } from 'react-redux';
import { IStaff } from '../../../models/employees.model';
import { doCheckStaffId, doCheckStaffMobilePhone, doUpdatePassword } from '../../../store/staff.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import styled from 'styled-components';
import { Button as AntButton, DatePicker, Descriptions, Modal, Select } from 'antd';
import { useSnackbar } from 'notistack';
import { CgCloseR } from 'react-icons/cg';

import Radio from '../../Radio';
import Button from '../../Button';
import FileUpload from '../../FileUpload';
import { errorVariant, infoVariant, warningVariant } from '../../../lib/barVariant';
import useNotification from '../../../hooks/useNotification';
import ConfirmModal from '../../modals/ConfirmModal';
import useAxios from '../../../hooks/useAxios';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import { deleteCampusFromEmployee } from '../../../api/employees.api';
import AddCampusModal from './modals/AddCampusModal';
import SelectCampus from '../../SelectCampus';
import { campusHeadOffice } from '../../../lib/data';

const { Option } = Select;

interface IProps {
  staff: IStaff|null;
  onSubmit: Function;
}

interface IMatch {
  id: string;
}

const Block = styled.div`
  .img-preview {
    max-width: 300px;
  }
`;

const CampusStaffEditor: FC<IProps> = ({
  staff,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { groups } = useSelector((state: RootState) => state.group);
  const { infoNotification, successNotification } = useNotification();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch<IMatch>();
  const memberCode = String(match.params.id);
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [hqYn, setHqYn] = useState(true);
  // 등록 시 캠퍼스
  const [campus, setCampus] = useState<string|null>(null);
  // 수정 시 캠퍼스
  const [campuses, setCampuses] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [engName, setEngName] = useState('');
  const [id, setId] = useState('');
  const [isIdChecked, setIsIdChecked] = useState(false);
  const [isMobilePhoneChecked, setIsMobilePhoneChecked] = useState(false);
  const [password, setPassword] = useState('');
  const [rePassword, setRePassword] = useState('');
  const [group, setGroup] = useState<any>(null);
  const [groupList, setGroupList] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [tel, setTel] = useState('');
  const [phone, setPhone] = useState('');
  const [birthday, setBirthday] = useState('');
  const [firstday, setFirstday] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [address, setAddress] = useState('');
  const [detailedAddress, setDetailedAddress] = useState('');
  const [file, setFile] = useState<any>(null);
  const [img, setImg] = useState<any>();
  const [imgBase64, setImgBase64] = useState('');
  const [postcodeVisible, setPostcodeVisible] = useState(false);
  const [updatePasswordVisible, setUpdatePasswordVisible] = useState(false);
  const [isHelper, setIsHelper] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [campusCode, setCampusCode] = useState('');
  const [addCampusVisible, setAddCampusVisible] = useState(false);
  const { currentCampus } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (auth) {
      if (auth.hqYn === 'Y') {
        setHqYn(true);
      } else {
        setHqYn(false);
      }
    }
  }, [auth]);

  useEffect(() => {
    if (groups) {
      const _groups: any = [];

      groups.forEach(item => {
        if (item.hqYn === 'Y') {
          if (hqYn) {
            _groups.push({
              label: item.korName,
              value: item.groupNo,
            });
          }
        } else {
          if (!hqYn) {
            _groups.push({
              label: item.korName,
              value: item.groupNo,
            });  
          }
        }
      });

      setGroupList(_groups);
    }
  }, [hqYn, groups]);

  useEffect(() => {
    if (staff) {
      setHqYn(staff.hqYn === 'Y' ? true : false);
      setName(staff.memberName || '');
      setEngName(staff.memberEngName || '');
      setId(staff.webId || '');
      setEmail(staff.email || '');
      setTel(staff.telephoneNo || '');
      setPhone(staff.mobilePhone || '');
      setZipcode(staff.zipNo || '');
      setAddress(staff.address || '');
      setDetailedAddress(staff.detailAddress || '');
      setImg(staff.img);

      if (staff.birthDay) {
        setBirthday(`${staff.birthDay.slice(0, 4)}-${staff.birthDay.slice(4, 6)}-${staff.birthDay.slice(6)}`);
      } else {
        setBirthday('');
      }

      if (staff.enterDate) {
        setFirstday(`${staff.enterDate.slice(0, 4)}-${staff.enterDate.slice(4, 6)}-${staff.enterDate.slice(6)}`);
      } else {
        setFirstday('');
      }

      setCampuses(staff.campusCode);
      onSetGroup(staff.groupNo);
    }
  }, [staff]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      setCampuses(campuses.filter(code => code !== campusCode));
      setCampusCode('');
      setConfirmDeleteVisible(false);
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const onSetHqYn = (value: boolean) => {
    setHqYn(value);
    if (value) setGroup(groupList[0].value);
    else setGroup(groupList[1].value);
  }

  const checkId = async (id: string) => {
    if (!id) return;

    const res = await dispatch(doCheckStaffId(id));

    switch (res?.status) {
      case 200:
        setIsIdChecked(true);
        break;
      case 400:
        setIsIdChecked(false);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const checkMobilePhone = async (phone: string) => {
    if (!phone) return;

    const res = await dispatch(doCheckStaffMobilePhone(phone));

    switch (res?.status) {
      case 200:
        setIsMobilePhoneChecked(true);
        break;
      case 400: {
        setIsMobilePhoneChecked(staff?.mobilePhone === phone) // 현재 등록되어 있는 전화번호와 입력한 번호가 같으면 OK (직원 수정 페이지 용)
        break;
      }
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
    
  }

  const onSetId = (value: string) => {
    setId(value);
    if (value.length >= 4) checkId(value);
  }

  const onSetMobilePhone = (value: string) => {
    setPhone(value);
    if (value.length >= 4) checkMobilePhone(value);
  }

  const onSetFile = (file: any) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (base64) setImgBase64(base64.toString());
    }
    reader.readAsDataURL(file);
    setFile(file);
  }

  const onCompletePostcode = (data: any) => {
    setAddress(data.jibunAddress);
    setZipcode(data.zonecode);
    setPostcodeVisible(false);
  }

  const onSave = async () => {
    if (!hqYn && auth.campus.length > 1 && !campus && !campuses.length && !currentCampus) {
      return enqueueSnackbar(t('campus.staff.message9'), infoVariant);
    }
    if (!name) {
      return enqueueSnackbar(t('campus.staff.message10'), infoVariant);
    }
    if (!engName) {
      return enqueueSnackbar(t('campus.staff.message11'), infoVariant);
    }
    if (!staff && !isIdChecked) {
      return enqueueSnackbar(t('campus.staff.message12'), infoVariant);
    }
    if (!staff && !password) {
      return enqueueSnackbar(t('campus.staff.message13'), infoVariant);
    }
    if (!staff && checkValidPassword(password)) {
      return enqueueSnackbar(t('campus.staff.message20'), infoVariant);
    }
    if (!group) {
      return enqueueSnackbar(t('campus.staff.message14'), infoVariant);
    }
    if (!phone) {
      return infoNotification(t('campus.staff.message15'));
    }
    let regex = /^\d+$/;
    if (!regex.test(phone)) {
      return infoNotification(t('campus.staff.message16'));
    }

    if(staff) {
      if(staff.mobilePhone !== phone && !isMobilePhoneChecked) {
        return enqueueSnackbar(t('campus.staff.message22'), infoVariant);  
      }
    }

    regex = /.+@.+/;
    if (!email || email.length < 1) {
      return enqueueSnackbar(t('campus.staff.message17'), infoVariant);
    }

    if (email && !regex.test(email)) {
      return enqueueSnackbar(t('campus.staff.message17'), infoVariant);
    }
  /* 
    이메일 체크 조건 삭제 - geunhyung.kim (2024-08-22)
    ㄴ CANB 시스템 기능개선_홈페이지수납공통_r135_아이디 찾기 비밀번호 변경 기능 제공_SB_v1.2
    
    if (email && !checkValidEmail(email)) {
      return enqueueSnackbar(t('campus.staff.message21'), infoVariant);
    }
  */
    

    

    const form = new FormData();
    form.append('hqYn', hqYn ? 'Y' : 'N');
    form.append('memberName', name);
    form.append('memberEngName', engName);
    form.append('groupNo', group);

    if (!staff) {
      form.append('webId', id);
      form.append('webPass', password);

      if (hqYn) {
        form.append('campusCode', campusHeadOffice);
      } else {
        if (campuses.length) {
          form.append('campusCode', campuses[0]);
        } else if (campus) {
          form.append('campusCode', campus);
        } else {
          form.append('campusCode', currentCampus);
        }
      }

      /*
      if (auth.hqYn === 'N' && auth.campus.length === 1) {
        form.append('campusCode', auth.campus[0]);
      } else if (hqYn) {
        form.append('campusCode', campusData[0].campusCode);
      } else if (campus) {
        form.append('campusCode', campus);
      } else if (campuses.length) {
        form.append('campusCode', campuses[0]);
      }
      */
    }

    if (email) form.append('email', email);
    if (tel) form.append('telephoneNo', tel);
    if (phone) form.append('mobilePhone', phone);
    if (birthday) form.append('birthDay', birthday.slice(0, 4) + birthday.slice(5, 7) + birthday.slice(8));
    if (firstday) form.append('enterDate', firstday.slice(0, 4) + firstday.slice(5, 7) + firstday.slice(8));
    if (zipcode) form.append('zipNo', zipcode);
    if (address) form.append('address', address);
    if (detailedAddress) form.append('detailAddress', detailedAddress);

    // 첨부파일 처리
    if (staff) {
      if (file) {
        if (img) {
          form.append('fileStatus', 'update');
        } else {
          form.append('fileStatus', 'create');
        }
      } else {
        if (img && !img.originlaName) {
          form.append('fileStatus', 'delete');
        }
      }
    }
    
    if (file) {
      form.append('file', file);
    }

    onSubmit(form);
  }

  // 비밀번호 체크
  const checkValidPassword = (password: string) => {
    const passwordPattern = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^\w\s]).{8,20}$/;
    return !passwordPattern.test(password);
  }

  const onUpdatePassword = async () => {
    
    if (!password) {
      return infoNotification(t('campus.staff.message13'));
    }

    if(password !== rePassword) {
      return infoNotification(t('campus.staff.message23'));
    }

    if(checkValidPassword(password)) {
      return infoNotification(t('campus.staff.message20'));
    }

    const res = await dispatch(doUpdatePassword(memberCode, {
      webPass: password
    }));

    switch (res?.status) {
      case 200:
        successNotification(t('campus.staff.message19'));
        setPassword('');
        setRePassword('');
        setUpdatePasswordVisible(false);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onDeleteCampus = (index: number) => {
    if (campuses.length === 1) {
      return infoNotification(t('campus.staff.1'));
    }

    setCampusCode(campuses[index]!);
    setConfirmDeleteVisible(true);
  }

  const onDeleteDbCampus = () => {
    if (!staff?.memberCode || !campusCode) return;
    deleteExecute(deleteCampusFromEmployee(staff.memberCode, campusCode));
  }

  const onSetCampus = (index: number, value: string) => {
    const _campuses = [...campuses];
    _campuses[index] = value;
    setCampuses(_campuses);
  }

  const onSetGroup = (value: any) => {
    const _group = groups?.find((item: any) => item.groupNo === value);
    if (_group && _group.authGroupDiv === 'S') {
      setIsHelper(true);
    } else {
      setIsHelper(false);
    }

    setGroup(value);
  }

  const checkValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@edu-poly\.com$/;
    return emailPattern.test(email);
  }

  return (
    <Block>
      {deleteIsLoading && <Loader />}
      <ErrorResponse error={deleteError} />

      <div className='comm-table__area rowType mt-0'>
        <Descriptions
          bordered
          size="small"
          column={2}
          labelStyle={{ width: '15%' }}
          contentStyle={{ width: '35%' }}
        >
          {
            auth.hqYn === 'Y' || auth.campus.length > 1
              ? (
                <>
                  {
                    auth.hqYn === 'Y'
                      ? (
                      <Descriptions.Item label={`* ${t('campus.staff.column38')}`} span={2}>
                        <div className="radio-wrapper">
                          <Radio
                            checked={hqYn}
                            onChange={() => onSetHqYn(true)}
                            text={t('campus.staff.option1')}
                            disabled={Boolean(staff)}
                          />
                          <Radio
                            checked={!hqYn}
                            onChange={() => onSetHqYn(false)}
                            text={t('campus.staff.option2')}
                            disabled={Boolean(staff)}
                          />
                        </div>
                      </Descriptions.Item>
                      )
                      : null
                  }
                  {
                    hqYn || isHelper || (auth.hqYn === 'N' && auth.campus.length === 1)
                      ? null
                      : (
                        <Descriptions.Item label={t('campus.staff.column39')} span={2}>
                          {
                            staff
                              ? null
                              : (
                                <div className="row mb-2">
                                  <div className="w-210p ml-2">
                                  <SelectCampus
                                    className="w-100"
                                    placeholder={t('campus.staff.placeholder2')}
                                    value={campus}
                                    onChange={setCampus}
                                    disabled={hqYn}
                                  />
                                  </div>
                                </div>
                                )
                          }
                          {
                            campuses.length > 0
                              ? campuses.map((campus: any, index: number) => (
                                <div className="row mb-2" key={index}>
                                  <div className="w-210p ml-2">
                                    <Select
                                      className="w-100"
                                      placeholder={t('campus.staff.placeholder2')}
                                      value={campuses[index]}
                                      onChange={value => onSetCampus(index, value)}
                                      disabled={hqYn}
                                    >
                                      {
                                        campusData?.slice(1).map((item: any) => {
                                          return (
                                            <Option
                                              key={item.campusCode}
                                              value={item.campusCode}
                                            >
                                              {item.campusKorName}
                                            </Option>
                                          )
                                        })
                                      }
                                    </Select>
                                  </div>
                                  <div className='comm-btn__area ml-2'>
                                    <Button
                                      className="btn-basic gray small"
                                      onClick={() => onDeleteCampus(index)}
                                      text={t('common.button.delete')}
                                    />
                                  </div>
                                </div>
                              ))
                              : null
                          }
                          {staff && (
                            <div className='comm-btn__area'>
                              <Button
                                className="btn-basic pink-line"
                                onClick={() => setAddCampusVisible(true)}
                                text={t('common.button.add')}
                              />
                            </div>
                          )}
                        </Descriptions.Item>
                      )
                  }
                </>
              )
              : null
          }
          <Descriptions.Item label={`* ${t('campus.staff.column40')}`}>
            <input
              type="text"
              className="form-control input-text"
              value={name}
              onChange={e => setName(e.target.value)}
              autoComplete="new-password"
            />
          </Descriptions.Item>
          <Descriptions.Item label={`* ${t('campus.staff.column41')}`}>
            <input
              type="text"
              className="form-control input-text"
              value={engName}
              onChange={e => setEngName(e.target.value)}
              autoComplete="new-password"
            />
          </Descriptions.Item>
          <Descriptions.Item label={
            [
              `* ${t('campus.staff.column36')}`, 
              <br />, 
              (staff?.webId !== id && id.length < 4) && `※ ${t('campus.staff.text9')}`
            ]}>
            <input
              type="text"
              className="form-control input-text"
              value={id}
              onChange={e => onSetId(e.target.value)}
              readOnly={Boolean(staff)}
              autoComplete="off"
            />
            <span className={'check-result' + (staff?.webId === id || !id || id.length < 4 ? '' : isIdChecked ? ' text-success' : ' text-danger')}>
              &nbsp;
              {
                staff?.webId === id
                  ? null
                  : id.length >= 4
                    ? isIdChecked
                      ? t('campus.staff.text7')
                      : t('campus.staff.text8')
                    : t('campus.staff.text9')
              }
            </span>
          </Descriptions.Item>
          <Descriptions.Item label={
            [
              `* ${t('campus.staff.column42')}`,
              <br/>,
              `※ ${t('campus.staff.text10')}`
            ]}
          >
            {
              staff
                ? (
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic mint"
                      onClick={() => setUpdatePasswordVisible(true)}
                      text={t('campus.staff.button9')}
                    />
                  </div>
                )
                : (
                  <input
                    type="password"
                    className="form-control input-text"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="off"
                  />
                )
            }
          </Descriptions.Item>
          <Descriptions.Item label={`* ${t('common.column.group')}`}>
            <div className="w-210p text-left">
              <Select
                className="w-100"
                placeholder={t('campus.staff.placeholder2')}
                value={group}
                onChange={value => onSetGroup(value)}
              >
                {
                  groupList?.map((item: any) => {
                    return (
                      <Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Option>
                    )
                  })
                }
              </Select>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={`* ${t('common.text.phoneNum')}`}>
            <input
              type="text"
              className="form-control input-text"
              placeholder={t('campus.staff.placeholder3')}
              value={phone}
              onChange={e => onSetMobilePhone(e.target.value)}
              autoComplete="off"
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.staff.column34')}>
            <input
              type="text"
              className="form-control input-text"
              placeholder={t('campus.staff.placeholder3')}
              value={tel}
              onChange={e => setTel(e.target.value)}
              autoComplete="off"
            />
          </Descriptions.Item>
          <Descriptions.Item label={
            [
              `* ${t('common.text.eMail')}`
            ]}>
            <input
              type="text"
              className="form-control input-text"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="off"
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.staff.column37')}>
            <div className="date-picker__area">
              <DatePicker
                value={birthday ? moment(birthday) : undefined}
                onChange={e => {
                  if (e === null) setBirthday('');
                  else setBirthday(moment(e).format('YYYY-MM-DD'))
                }}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.staff.column43')}>
            <div className="date-picker__area">
              <DatePicker
                value={firstday ? moment(firstday) : undefined}
                onChange={e => {
                  if (e === null) setFirstday('');
                  else setFirstday(moment(e).format('YYYY-MM-DD'))
                }}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('common.text.address1')} span={2}>
            <div className="comm-btn__area">
              <Button
                className="btn-basic black"
                onClick={() => setPostcodeVisible(true)}
                text={t('common.button.address')}
              />
              <span className='v-line'>{zipcode ? ` (${zipcode})` : ''} {address}</span>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('common.text.address2')} span={2}>
            <input
              type="text"
              className="form-control input-text"
              value={detailedAddress}
              onChange={e => setDetailedAddress(e.target.value)}
              autoComplete="off"
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('campus.staff.column44')} span={2}>
            {
              img?.originalName
                ? (
                  <div>
                    <a
                      className="download-link"
                      href={img.fileLocation}
                      download={img.originalName}
                    >
                      {img.originalName}
                    </a>
                    <span
                      className="ml-1 pointer"
                      onClick={() => setImg({ ...img, originalName: '' })}
                    >
                      <CgCloseR />
                    </span>
                  </div>
                )
                : null
            }
            <FileUpload
              file={file}
              setFile={onSetFile}
            />
            {
              imgBase64
                ? (
                  <>
                    <br />
                    <img className="img-preview" src={imgBase64} alt="preview" />
                  </>
                )
                : null
            }
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSave}
            className="btn-basic pink"
            text={t('common.button.save')}
          />
          <Button
            onClick={() => history.push(`/campus/staff${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      {/* 주소검색 모달 */}
      <Modal
        title={t('common.button.address')}
        visible={postcodeVisible}
        onCancel={() => setPostcodeVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              key="submit"
              className='btn-basic gray'
              onClick={() => setPostcodeVisible(false)}
            >
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <Postcode
          onComplete={onCompletePostcode}
          autoClose={false}
        />
      </Modal>

      {/* 비밀번호 변경 모달 */}
      <Modal
        title={t('campus.staff.button9')}
        visible={updatePasswordVisible}
        onCancel={() => {
          setPassword('');
          setRePassword('');
          setUpdatePasswordVisible(false);
        }}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onUpdatePassword}
            >
              {t('campus.staff.button11')}
            </AntButton>
          </div>
        ]}
      >
        <div className="row justify-content-center">
          <div className="col-6 text-center">
            {`${t('campus.staff.text12')}`}
            <input
              type="password"
              className="form-control input-text w-210p"
              placeholder={t('campus.staff.placeholder4')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="off"
            />
            <input
              type="password"
              className="form-control input-text w-210p mt-2"
              placeholder={t('campus.staff.placeholder5')}
              value={rePassword}
              onChange={e => setRePassword(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className='mt-2' >※ {t('campus.staff.text10')}</div>
        </div>
      </Modal>

      {confirmDeleteVisible &&
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDeleteDbCampus}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      }
      {addCampusVisible && (
        <AddCampusModal
          visible={addCampusVisible}
          setVisible={setAddCampusVisible}
          memberCode={staff?.memberCode}
          campusList={campusData?.slice(1)}
          campuses={campuses}
          setCampuses={setCampuses}
        />
      )}
    </Block>
  )
}

export default CampusStaffEditor;
